import React, { useState, useRef, useEffect, useCallback, useImperativeHandle } from 'react';
import { Box, Button, TextField, Typography, IconButton, Tooltip, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { utmMauritania1999ToLatLng, latLngToUtmMauritania1999 } from '../utils/utm';
import * as turf from '@turf/turf';


// Uncomment Draggable import
let Draggable: any = null;
try {
  Draggable = require('react-draggable').default;
} catch {}

// Area mode coordinate row interface
export interface CoordinateRow {
  x: string;
  y: string;
}

export type DatumOption = 'UTM28' | 'UTM29' | 'UTM30' | 'DD';

// Function to calculate planar area from UTM coordinates
const calculatePlanarArea = (coords: CoordinateRow[]): number => {
  const points = coords
    .map(c => ({ x: parseFloat(c.x), y: parseFloat(c.y) }))
    .filter(p => !isNaN(p.x) && !isNaN(p.y));

  if (points.length < 3) {
    return 0;
  }

  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }

  return Math.abs(area / 2); // Area in square meters
};

const isValidLat = (lat: number) => lat >= -90 && lat <= 90;
const isValidLng = (lng: number) => lng >= -180 && lng <= 180;



interface CoordinateSearchProps {
  onFind: (lat: number, lng: number) => void;
  onClose?: () => void;
  onTempMarker?: (coords: { lat: number; lng: number } | null) => void;
  pins: { lat: string; lng: string; id: string; coordType?: string; zone?: string; easting?: string; northing?: string; }[];
  onPin: (point: { lat: string; lng: string; id: string; coordType?: string; zone?: string; easting?: string; northing?: string; }) => void;
  onRemovePin: (id: string) => void;
  onClearPins: () => void;
  onPinModeChange?: (isPinMode: boolean) => void;
  // Area mode props
  onManualGeometryChange?: (geometry: GeoJSON.Geometry | null) => void;
  onManualZoomRequest?: () => void;
  onAreaMarkersChange?: (markers: Array<{ lat: number; lng: number; id: string }>) => void;
}


const CoordinateSearchComponent = (
  { onFind, onClose, onTempMarker, pins, onPin, onRemovePin, onClearPins, onPinModeChange, onManualGeometryChange, onManualZoomRequest, onAreaMarkersChange }: CoordinateSearchProps,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const [coordType, setCoordType] = useState<'POINT' | 'AREA'>('POINT');
  const [pointMode, setPointMode] = useState<'XY' | 'PIN'>('XY');
  const [coordinateFormat, setCoordinateFormat] = useState<'DD' | 'UTM28' | 'UTM29' | 'UTM30'>('DD');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [easting, setEasting] = useState('');
  const [northing, setNorthing] = useState('');
  const [lastSearched, setLastSearched] = useState<{ lat: number; lng: number } | null>(null);
  const [error, setError] = useState('');

  // Area mode state
  const [manualCoords, setManualCoords] = useState<CoordinateRow[]>([{ x: '', y: '' }]);
  const [manualArea, setManualArea] = useState<string | null>(null);

  // Polygon submission state






  // Add a ref to track if we've already pinned the first point
  const hasPinnedFirstPoint = useRef(false);

  // Add a ref to track if we've already zoomed to the first point
  const hasZoomedToFirstPoint = useRef(false);

  const dragHandleRef = useRef<HTMLDivElement>(null);

  // Copy coordinates to clipboard
  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log('Coordinates copied to clipboard');
    } catch (err) {
      console.error('Failed to copy coordinates:', err);
    }
  }, []);

  // Area mode coordinate processing
  useEffect(() => {
    if (coordType !== 'AREA') {
      setManualArea(null);
      if (onManualGeometryChange) onManualGeometryChange(null);
      // Reset the pin tracking when switching away from AREA mode
      hasPinnedFirstPoint.current = false;
      // Clear manual coordinates when switching away from AREA mode
      setManualCoords([{ x: '', y: '' }]);
      // Reset zoom tracking when switching away from AREA mode
      hasZoomedToFirstPoint.current = false;
      return;
    }
    
    console.log('Processing coordinates:', { coordinateFormat, manualCoords });
    
    // Parse valid coordinates (only complete rows with minimum character requirements)
    let lngLatCoords: [number, number][] = [];
    
    if (coordinateFormat.startsWith('UTM')) {
      try {
        // Extract zone number from datum (e.g., 'UTM28' -> 28)
        const zone = parseInt(coordinateFormat.replace('UTM', ''));
        console.log('UTM zone:', zone);
        
        lngLatCoords = manualCoords.map(({ x, y }) => {
          // Only process complete rows (both X and Y present)
          if (x === '' || y === '') return null;
          
          const xVal = Number(x);
          const yVal = Number(y);
          
          if (!Number.isFinite(xVal) || !Number.isFinite(yVal)) return null;
          
          console.log('Converting UTM:', { x: xVal, y: yVal, zone });
          
          // Use the same UTM conversion logic as Point mode
          const converted = utmMauritania1999ToLatLng(xVal, yVal, zone);
          
          console.log('UTM conversion result:', converted);
          
          if (isNaN(converted.lat) || isNaN(converted.lng)) return null;
          
          return [converted.lng, converted.lat];
        }).filter(Boolean) as [number, number][];
        
        console.log('Final UTM coordinates:', lngLatCoords);
      } catch (error) {
        console.error('UTM conversion error:', error);
        setManualArea(null);
        if (onManualGeometryChange) onManualGeometryChange(null);
        return;
      }
    } else {
      // DD coordinates: UI order is Latitude (first column) then Longitude (second column)
      lngLatCoords = manualCoords.map(({ x, y }) => {
        // Only process complete rows (both X and Y present)
        if (x === '' || y === '') return null;
        
        const lat = Number(x);
        const lng = Number(y);
        
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
        
        // Validate DD coordinates
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
        
        return [lng, lat];
      }).filter(Boolean) as [number, number][];
      
      console.log('Final DD coordinates:', lngLatCoords);
    }
    
    // Trigger zoom only when we have exactly one complete coordinate
    if (onManualZoomRequest && lngLatCoords.length === 1) {
      const firstRow = manualCoords[0];
      if (firstRow && firstRow.x !== '' && firstRow.y !== '') {
        if (!hasZoomedToFirstPoint.current) {
          console.log('Triggering zoom request for first complete point');
          onManualZoomRequest();
          hasZoomedToFirstPoint.current = true;
        }
      }
    } else if (lngLatCoords.length > 1) {
      // Don't trigger zoom for subsequent points - only draw geometry
      console.log('Drawing geometry for multiple points without zoom');
      // Reset zoom tracking since we now have multiple points
      hasZoomedToFirstPoint.current = false;
    } else if (lngLatCoords.length === 0) {
      // Reset zoom tracking when no coordinates are present
      hasZoomedToFirstPoint.current = false;
    }
    
    // Emit geometry for preview (only for complete rows with proper character count)
    if (onManualGeometryChange) {
      if (lngLatCoords.length === 1) {
        const geometry: GeoJSON.Point = { type: 'Point', coordinates: lngLatCoords[0] };
        console.log('Emitting Point geometry:', geometry);
        onManualGeometryChange(geometry);
      } else if (lngLatCoords.length === 2) {
        const geometry: GeoJSON.LineString = { type: 'LineString', coordinates: lngLatCoords };
        console.log('Emitting LineString geometry:', geometry);
        onManualGeometryChange(geometry);
      } else if (lngLatCoords.length >= 3) {
        // Polygon: close if not closed
        const first = lngLatCoords[0];
        const last = lngLatCoords[lngLatCoords.length - 1];
        const closed = (first[0] === last[0] && first[1] === last[1]);
        const coords = closed ? lngLatCoords : [...lngLatCoords, [...first]];
        const geometry: GeoJSON.Polygon = { type: 'Polygon', coordinates: [coords] };
        console.log('Emitting Polygon geometry:', geometry);
        onManualGeometryChange(geometry);
      } else {
        console.log('Clearing geometry');
        onManualGeometryChange(null);
      }
    }

    // Create markers for each valid coordinate in Area mode
    if (onAreaMarkersChange && coordType === 'AREA') {
      const markers = lngLatCoords.map((coord, index) => ({
        lat: coord[1],
        lng: coord[0],
        id: `area-marker-${index}`
      }));
      console.log('Creating area markers:', markers);
      onAreaMarkersChange(markers);
    } else if (onAreaMarkersChange && coordType !== 'AREA') {
      // Clear area markers when not in AREA mode
      onAreaMarkersChange([]);
    }
    
    // Calculate area/distance
if (lngLatCoords.length >= 3) {
  try {
    let areaM2;
    if (coordinateFormat.startsWith('UTM')) {
      // Use planar calculation for UTM for higher accuracy
      areaM2 = calculatePlanarArea(manualCoords);
    } else {
      const poly = turf.polygon([lngLatCoords.map(c => [c[0], c[1]])]);
      areaM2 = turf.area(poly);
    }

    const areaStr = `${(areaM2 / 1e6).toFixed(2)} km²`;
    setManualArea(areaStr);
  } catch (error) {
    console.error('Area calculation error:', error);
    setManualArea(null);
  }
} else if (lngLatCoords.length === 2) {
  // Line: show distance
  try {
    const line = turf.lineString(lngLatCoords);
    const length = turf.length(line, { units: 'kilometers' });
    setManualArea(`${length.toLocaleString(undefined, { maximumFractionDigits: 2 })} km`);
  } catch (error) {
    console.error('Distance calculation error:', error);
    setManualArea(null);
  }
} else {
  setManualArea(null);
}
}, [coordType, coordinateFormat, manualCoords, onManualGeometryChange, onManualZoomRequest, onAreaMarkersChange]);

// Handle polygon submission


// Area mode coordinate input functions
const handleCoordChange = (idx: number, key: 'x' | 'y', value: string) => {
// Ensure we have at least one row to work with
const currentCoords = manualCoords.length === 0 ? [{ x: '', y: '' }] : manualCoords;

const newCoords = currentCoords.map((row, i) =>
  i === idx ? { ...row, [key]: value } : row
);

// If we started with an empty array, now we have a real array
setManualCoords(newCoords);

// Don't trigger geometry updates here - only in the useEffect above
// This ensures we only draw when both X and Y are complete
};

const handleAddRow = () => {
if (manualCoords.length < 100) {
  setManualCoords([...manualCoords, { x: '', y: '' }]);
}
};

const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, idx: number) => {
if (e.key === 'Enter') {
  e.preventDefault();
  // If it's the last row, add a new one
  if (idx === manualCoords.length - 1) {
    handleAddRow();
  }
}
};

const handleRemoveRow = (idx: number) => {
setManualCoords(manualCoords.filter((_, i) => i !== idx));
// Reset zoom tracking when removing rows, as this might affect the first point
hasZoomedToFirstPoint.current = false;
  };

  const handleCopyRow = (idx: number) => {
    const row = manualCoords[idx];
    navigator.clipboard.writeText(`${row.x} - ${row.y}`);
  };

  const handleCopyAll = () => {
    const text = manualCoords
      .map((row, i) => `P${i + 1}: ${row.x} - ${row.y}`)
      .join('\n');
    navigator.clipboard.writeText(text);
  };

  const handleClearAll = () => {
    setManualCoords([{ x: '', y: '' }]);
    // Reset zoom tracking when clearing all coordinates
    hasZoomedToFirstPoint.current = false;
  };

  const handlePointPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const pastedText = e.clipboardData.getData('text');
    if (!pastedText.includes('\t')) return;

    e.preventDefault();
    const parts = pastedText.split('\t');
    const x = (parts[0] || '').trim();
    const y = (parts[1] || '').trim();

    if (coordinateFormat === 'DD') {
      setLat(x);
      setLng(y);
    } else {
      setEasting(x);
      setNorthing(y);
    }
  };

  const handleAreaPaste = (
    e: React.ClipboardEvent<HTMLDivElement>,
    rowIndex: number
  ) => {
    const pastedText = e.clipboardData.getData('text');
    e.preventDefault();

    // Normalize delimiters to tabs: commas, semicolons, and whitespace.
    // IMPORTANT: Do NOT touch '-' to preserve negative signs in values like -15.527346.
    const normalizeLine = (line: string) =>
      line
        .trim()
        .replace(/[;,]/g, '\t')
        .replace(/\s+/g, '\t');

    const lines = pastedText.trim().split(/[\r\n]+/);
    const pastedCoords = lines
      .map(line => normalizeLine(line))
      .map(line => {
        const parts = line.split('\t');
        // Keep only first two tokens if extra content exists on a line
        const x = (parts[0] || '').trim();
        const y = (parts[1] || '').trim();
        return { x, y };
      })
      .filter(c => c.x || c.y);

    if (pastedCoords.length === 0) return;

    // Create a new array to avoid direct mutation
    const newCoords = [...manualCoords];
    
    // Replace or add coordinates from the paste
    pastedCoords.forEach((coord, index) => {
      const targetIndex = rowIndex + index;
      if (targetIndex < newCoords.length) {
        // Overwrite existing row
        newCoords[targetIndex] = coord;
      } else {
        // Add new row
        newCoords.push(coord);
      }
    });

    setManualCoords(newCoords);
  };



  const handleFind = useCallback(() => {
    if (pointMode === 'XY') {
      if (coordinateFormat === 'DD') {
      const latNum = parseFloat(lat);
      const lngNum = parseFloat(lng);
        
      if (isNaN(latNum) || isNaN(lngNum)) {
          setError('Please enter valid latitude and longitude values.');
        return;
      }
        
      if (!isValidLat(latNum) || !isValidLng(lngNum)) {
          setError('Latitude must be between -90 and 90, and longitude between -180 and 180.');
        return;
      }
        
      setError('');
      setLastSearched({ lat: latNum, lng: lngNum });
      onFind(latNum, lngNum);
        
        // Add temporary marker for 5 seconds
      if (onTempMarker) {
        onTempMarker({ lat: latNum, lng: lngNum });
          setTimeout(() => {
            // Clear temporary marker after 5 seconds
            if (onTempMarker) onTempMarker(null);
          }, 5000);
        }
        
        // Convert to UTM for display
        try {
          const zoneNum = coordinateFormat.startsWith('UTM') ? parseInt(coordinateFormat.slice(3)) : 28;
          const utmResult = latLngToUtmMauritania1999(latNum, lngNum, zoneNum);
          console.log(`Converted to UTM Zone ${zoneNum}: ${utmResult.x.toFixed(2)}E, ${utmResult.y.toFixed(2)}N`);
      } catch (e) {
        console.error('Error converting DD to UTM:', e);
      }
    } else { // UTM
      const eastingNum = parseFloat(easting);
      const northingNum = parseFloat(northing);
        const zoneNum = parseInt(coordinateFormat.slice(3));
        
      if (isNaN(eastingNum) || isNaN(northingNum)) {
          setError('Please enter valid easting and northing values.');
        return;
      }
        
        setError('');
        
        try {
          const result = utmMauritania1999ToLatLng(eastingNum, northingNum, zoneNum);
          setLastSearched({ lat: result.lat, lng: result.lng });
          onFind(result.lat, result.lng);
          
          // Add temporary marker for 5 seconds
        if (onTempMarker) {
            onTempMarker({ lat: result.lat, lng: result.lng });
            setTimeout(() => {
              // Clear temporary marker after 5 seconds
              if (onTempMarker) onTempMarker(null);
            }, 5000);
          }
        } catch (e) {
          setError('Error converting UTM coordinates. Please check your input.');
          console.error('Error converting UTM to Lat/Lng:', e);
        }
      }
    }
  }, [pointMode, coordinateFormat, lat, lng, easting, northing, onFind, onTempMarker]);

  useEffect(() => {
    if (pointMode === 'XY') {
      if (coordinateFormat === 'DD') {
      setEasting('');
      setNorthing('');
      } else {
      setLat('');
      setLng('');
      }
    } else if (pointMode === 'PIN') {
      // No specific fields to clear for PIN mode, as it's just for placing a pin
    }
    if (onPinModeChange) {
      onPinModeChange(pointMode === 'PIN');
    }
  }, [pointMode, coordinateFormat, onPinModeChange]);

  useImperativeHandle(ref, () => dragHandleRef.current!);

  // Use Draggable if available, but ensure popup is always visible and overlays map/header
  // Single draggable header with Projection, CRS label, and close button
  const BoxContent = (
    <Box sx={{ p: 1.5, pb: 0.8 }}>
      {/* XY/Surface toggle */}
      {/* Sub-tabs for POINT mode */}
      {coordType === 'POINT' && (
        <>
          {/* XY/PIN Toggle only */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
            {/* XY/PIN Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '2px', gap: 0, fontFamily: 'Louguiya, sans-serif' }}>
              {['XY', 'PIN'].map((type) => (
            <Box
              key={type}
              sx={{
                    px: 1.2,
                    py: 0.4,
                    borderRadius: '6px',
                fontWeight: 700,
                    fontSize: '0.75rem',
                fontFamily: 'Louguiya, sans-serif',
                    color: pointMode === type ? '#fff' : '#bbb',
                    backgroundColor: pointMode === type ? 'rgba(44, 143, 219, 0.8)' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                '&:hover': {
                      backgroundColor: pointMode === type ? 'rgba(44, 143, 219, 0.9)' : 'rgba(255, 255, 255, 0.1)',
                },
              }}
                  onClick={() => setPointMode(type as 'XY' | 'PIN')}
              tabIndex={0}
              role="button"
                  aria-pressed={pointMode === type}
            >
              {type}
            </Box>
          ))}
        </Box>
      </Box>

          {/* Content for POINT mode */}
          {pointMode === 'PIN' ? (
            <Box sx={{ 
              textAlign: 'center', 
              my: 2, 
              p: 1.5,
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              <Typography sx={{ 
                color: '#fff', 
                fontSize: '0.85rem', 
                fontWeight: 600,
                mb: 0.8,
                fontFamily: 'Louguiya, sans-serif'
              }}>
                Mode Épinglage Actif
        </Typography>
              <Typography sx={{ 
                color: '#ccc', 
                fontSize: '0.75rem',
                fontFamily: 'Louguiya, sans-serif'
              }}>
                Cliquez sur la carte pour placer une épingle
              </Typography>
            </Box>
          ) : pointMode === 'XY' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mb: 1.5 }}>
              {/* Input Fields based on format */}
              {coordinateFormat === 'DD' ? (
                <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
                  {/* Clear button */}
                  <IconButton 
                    onClick={() => { setLat(''); setLng(''); setLastSearched(null); }} 
                    size="small" 
                    sx={{ 
                      color: '#ff6b6b', 
                      p: 0.8, 
                      minWidth: '32px',
                      minHeight: '32px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    ×
                  </IconButton>

                  {/* Latitude field */}
          <TextField
            label="Latitude"
            value={lat}
                    onChange={(e) => {
                      console.log('Latitude onChange:', e.target.value);
                      setLat(e.target.value);
                    }}
                    onPaste={handlePointPaste}
                    onKeyDown={(e) => e.key === 'Enter' && handleFind()}
                    onFocus={(e) => e.target.select()}
                    size="small"
            className="coordinate-input"
                    InputLabelProps={{ shrink: lat !== '', style: { fontSize: '0.7rem', color: '#fff', fontWeight: 500 } }}
                    inputProps={{ style: { fontSize: '0.75rem', fontWeight: 500, color: '#fff' } }}
            sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        minHeight: '32px',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2c8fdb',
                  borderWidth: 2,
                          boxShadow: '0 0 8px rgba(44, 143, 219, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2c8fdb',
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          borderWidth: 1.5,
                },
                },
                      '& .MuiInputLabel-root': {
                        color: '#fff',
                        fontSize: '0.7rem',
                        fontWeight: 500,
              },
                      '& .MuiInputBase-input': {
                        fontSize: '0.75rem',
                        color: '#fff',
                        fontWeight: 500,
                      }
            }}
          />
                  
                  {/* Longitude field */}
          <TextField
            label="Longitude"
            value={lng}
                    onChange={(e) => {
                      console.log('Longitude onChange:', e.target.value);
                      setLng(e.target.value);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleFind()}
                    onFocus={(e) => e.target.select()}
                    size="small"
            className="coordinate-input"
                    InputLabelProps={{ shrink: lng !== '', style: { fontSize: '0.7rem', color: '#fff', fontWeight: 500 } }}
                    inputProps={{ style: { fontSize: '0.75rem', fontWeight: 500, color: '#fff' } }}
            sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        minHeight: '32px',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2c8fdb',
                  borderWidth: 2,
                          boxShadow: '0 0 8px rgba(44, 143, 219, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2c8fdb',
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          borderWidth: 1.5,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#fff',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.75rem',
                        color: '#fff',
                        fontWeight: 500,
                      }
                    }}
                  />
                  
                  {/* Find button (search icon) */}
                  <IconButton 
                    onClick={handleFind}
                    size="small" 
                    sx={{ 
                      color: '#ccc', 
                      p: 0.8, 
                      minWidth: '32px',
                      minHeight: '32px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    🔍
                  </IconButton>
                  
                  {/* Pin button */}
                  <IconButton 
                    onClick={handleFind}
                    size="small" 
                    sx={{ 
                      color: '#ff6b6b', 
                      p: 0.8, 
                      minWidth: '32px',
                      minHeight: '32px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    📌
                  </IconButton>
        </Box>
      ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  {/* Clear button */}
                  <IconButton 
                    onClick={() => { setEasting(''); setNorthing(''); setLastSearched(null); }} 
                    size="small" 
                    sx={{ 
                      color: '#ff6b6b', 
                      p: 0.8, 
                      minWidth: '32px',
                      minHeight: '32px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    ×
                  </IconButton>

                  {/* X (Easting) field */}
          <TextField
            label="X (Easting)"
            value={easting}
            onChange={(e) => setEasting(e.target.value)}
                    onPaste={handlePointPaste}
            onKeyDown={(e) => e.key === 'Enter' && handleFind()}
            onFocus={(e) => e.target.select()}
            size="small"
            className="coordinate-input"
                    InputLabelProps={{ shrink: easting !== '', style: { fontSize: '0.7rem', color: '#fff', fontWeight: 500 } }}
                    inputProps={{ style: { fontSize: '0.75rem', fontWeight: 500, color: '#fff' } }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        minHeight: '32px',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2c8fdb',
                          borderWidth: 2,
                          boxShadow: '0 0 8px rgba(44, 143, 219, 0.3)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2c8fdb',
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          borderWidth: 1.5,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#fff',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.75rem',
                        color: '#fff',
                        fontWeight: 500,
                      }
                    }}
                  />
                  
                  {/* Y (Northing) field */}
          <TextField
            label="Y (Northing)"
            value={northing}
            onChange={(e) => setNorthing(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleFind()}
            onFocus={(e) => e.target.select()}
            size="small"
            className="coordinate-input"
                    InputLabelProps={{ shrink: northing !== '', style: { fontSize: '0.7rem', color: '#fff', fontWeight: 500 } }}
                    inputProps={{ style: { fontSize: '0.75rem', fontWeight: 500, color: '#fff' } }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        minHeight: '32px',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2c8fdb',
                          borderWidth: 2,
                          boxShadow: '0 0 8px rgba(44, 143, 219, 0.3)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2c8fdb',
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          borderWidth: 1.5,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#fff',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.75rem',
                        color: '#fff',
                        fontWeight: 500,
                      }
                    }}
                  />
                  
                  {/* Find button (search icon) */}
                  <IconButton 
                    onClick={handleFind}
                    size="small" 
                    sx={{ 
                      color: '#ccc', 
                      p: 0.8, 
                      minWidth: '32px',
                      minHeight: '32px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    🔍
                  </IconButton>
                  
                  {/* Pin button */}
                  <IconButton 
                    onClick={handleFind}
                    size="small" 
                    sx={{ 
                      color: '#ff6b6b', 
                      p: 0.8, 
                      minWidth: '32px',
                      minHeight: '32px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    📌
                  </IconButton>
                </Box>
              )}
            </Box>
          ) : null}
        </>
      )}

      {coordType === 'AREA' && (
        <Box sx={{ p: 1.5, pb: 0.8 }}>
          {/* Remove the duplicate mode selection buttons - they're already at the top */}
          
          {/* Area display - Centered and compact */}
{manualArea && (
  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      p: '4px 10px',
      backgroundColor: 'rgba(255,255,255,0.1)',
      borderRadius: '6px',
      border: '1px solid rgba(255,255,255,0.2)',
      width: 'fit-content'
    }}>
      <Typography sx={{
        color: '#fff',
        fontSize: '0.8rem',
        fontWeight: 600,
        fontFamily: 'Louguiya, sans-serif'
      }}>
        Area: {manualArea}
      </Typography>
      <Tooltip title="Copy all coordinates">
        <IconButton
          size="small"
          onClick={handleCopyAll}
          sx={{
            color: '#2c8fdb',
            p: 0.5,
            '&:hover': {
              backgroundColor: 'rgba(44, 143, 219, 0.1)'
            }
          }}
        >
          <ContentCopyIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Tooltip>
    </Box>
  </Box>
)}

{/* Coordinate input fields */}
<Box sx={{ mb: 1.5 }}>
  {/* Column headers */}
  <Box sx={{ display: 'flex', mb: 0.5, alignItems: 'center' }}>
    <Box sx={{ width: '25px' }} /> {/* Space for row number */}
    <Typography sx={{
      color: '#fff',
      fontSize: '0.7rem',
      fontWeight: 600,
              fontFamily: 'Louguiya, sans-serif',
      flex: 1,
      textAlign: 'center'
    }}>
      {coordinateFormat === 'DD' ? 'Latitude' : 'East (X)'}
    </Typography>
    <Typography sx={{
      color: '#fff',
      fontSize: '0.7rem',
      fontWeight: 600,
      fontFamily: 'Louguiya, sans-serif',
      flex: 1,
      textAlign: 'center'
    }}>
      {coordinateFormat === 'DD' ? 'Longitude' : 'North (Y)'}
    </Typography>
    <Box sx={{ width: '60px' }} /> {/* Space for action buttons */}
  </Box>

  {/* Coordinate rows */}
<Box sx={{
  maxHeight: '280px', // Max height for 8 rows approx
  overflowY: manualCoords.length > 8 ? 'auto' : 'hidden',
  pr: manualCoords.length > 8 ? 1 : 0, // Padding for scrollbar
}}>
  {manualCoords.map((row, idx) => (
    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
      {/* Add Row button (tiny +) */}
      <IconButton
        size="small"
        onClick={handleAddRow}
        disabled={manualCoords.length >= 100}
        sx={{
          color: '#2c8fdb',
          p: 0.2,
          minWidth: '20px',
          minHeight: '20px',
          fontSize: '0.8rem',
          '&:hover': {
            backgroundColor: 'rgba(44, 143, 219, 0.1)'
          },
          '&:disabled': {
            color: '#666',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }
        }}
      >
        +
      </IconButton>

      {/* Copy button */}
      <Tooltip title="Copy row">
        <IconButton
          size="small"
          onClick={() => handleCopyRow(idx)}
          sx={{
            color: '#2c8fdb',
            p: 0.3,
            '&:hover': {
              backgroundColor: 'rgba(44, 143, 219, 0.1)'
            }
          }}
        >
          <ContentCopyIcon sx={{ fontSize: '0.8rem' }} />
        </IconButton>
      </Tooltip>

      {/* Row number */}
      <Typography sx={{ color: '#aaa', fontSize: '0.7rem', width: '25px', textAlign: 'right', pr: 0.5 }}>
        {idx + 1}
      </Typography>

      <TextField
        value={row.x}
        onChange={(e) => handleCoordChange(idx, 'x', e.target.value)}
        onPaste={(e) => handleAreaPaste(e, idx)}
        onKeyDown={(e) => handleKeyDown(e, idx)}
        size="small"
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            minHeight: '32px',
            fontSize: '0.7rem',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2c8fdb',
              borderWidth: 1.5,
              boxShadow: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2c8fdb',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              boxShadow: 'none',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255,255,255,0.3)',
              borderWidth: 1.5,
              boxShadow: 'none',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 500
          },
          '& .MuiInputBase-input': {
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 500,
          }
        }}
        placeholder={coordinateFormat === 'DD' ? 'Lat' : 'X'}
      />
      <TextField
        value={row.y}
        onChange={(e) => handleCoordChange(idx, 'y', e.target.value)}
        onKeyDown={(e) => handleKeyDown(e, idx)}
        size="small"
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            minHeight: '32px',
            fontSize: '0.7rem',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2c8fdb',
              borderWidth: 1.5,
              boxShadow: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2c8fdb',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              boxShadow: 'none',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255,255,255,0.3)',
              borderWidth: 1.5,
              boxShadow: 'none',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 500
          },
          '& .MuiInputBase-input': {
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 500,
          }
        }}
        placeholder={coordinateFormat === 'DD' ? 'Long' : 'Y'}
      />
      
      {/* Remove row button */}
      {(manualCoords.length > 1 || idx === 0) && (
        <Tooltip title="Remove row">
          <IconButton
            size="small"
            onClick={() => handleRemoveRow(idx)}
            sx={{
              color: '#ff6b6b',
              p: 0.3,
              '&:hover': {
                backgroundColor: 'rgba(255, 107, 107, 0.1)'
              }
            }}
          >
            <CloseIcon sx={{ fontSize: '0.8rem' }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  ))}
</Box>
</Box>


        </Box>
      )}

      {/* Action buttons for POINT mode */}
      {coordType === 'POINT' && (
        <Box sx={{ display: 'flex', gap: 1.2, justifyContent: 'center', mt: 1.5 }}>
          {lastSearched && pointMode !== 'PIN' && (
          <Button
              onClick={() => {
                const pinData = {
                  lat: lastSearched.lat.toFixed(6), 
                  lng: lastSearched.lng.toFixed(6), 
                  id: '',
                  coordType: coordinateFormat,
                  zone: coordinateFormat.startsWith('UTM') ? coordinateFormat : undefined,
                  easting: coordinateFormat.startsWith('UTM') ? easting : undefined,
                  northing: coordinateFormat.startsWith('UTM') ? northing : undefined
                };
                onPin(pinData);
              }}
              sx={{
                fontSize: '0.8rem',
                px: 2.5,
                py: 0.8,
                backgroundColor: 'rgba(255, 193, 7, 0.2)',
                color: '#ffc107',
                border: '2px solid rgba(255, 193, 7, 0.4)',
                borderRadius: '10px',
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Louguiya, sans-serif',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(255, 193, 7, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 193, 7, 0.3)',
                  borderColor: '#ffc107',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(255, 193, 7, 0.3)',
                }
              }}
          >
            Pin
          </Button>
        )}
      </Box>
      )}

      {/* Pinned locations display */}
      {pins.length > 0 && (
        <Box sx={{ mt: 1.5, p: 1.2, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)' }}>
          <Typography sx={{ 
            color: '#fff', 
            fontSize: '0.75rem', 
            fontWeight: 600, 
            mb: 1.2,
            fontFamily: 'Louguiya, sans-serif',
            textAlign: 'center'
          }}>
            Pinned Locations ({pins.length})
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            {pins.map((pin, index) => (
              <Box key={pin.id} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 0.8,
                backgroundColor: 'rgba(255,255,255,0.03)',
                borderRadius: 1,
                border: '1px solid rgba(255,255,255,0.05)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Typography sx={{ 
                    color: '#2c8fdb', 
                    fontSize: '0.7rem', 
                    fontWeight: 600,
                    fontFamily: 'Louguiya, sans-serif'
                  }}>
                    {index + 1}.
                  </Typography>
                  <Typography sx={{ 
                    color: '#fff', 
                    fontSize: '0.7rem',
                    fontFamily: 'Louguiya, sans-serif'
                  }}>
                    {pin.coordType === 'DD' ? 
                      `Lat: ${parseFloat(pin.lat).toFixed(6)}, Lng: ${parseFloat(pin.lng).toFixed(6)}` :
                      pin.coordType && pin.coordType.startsWith('UTM') && pin.zone && pin.easting && pin.northing ?
                      `${pin.zone} ${pin.easting}E ${pin.northing}N` :
                      `Lat: ${parseFloat(pin.lat).toFixed(6)}, Lng: ${parseFloat(pin.lng).toFixed(6)}`
                    }
                  </Typography>
                  <Tooltip title="Copy coordinates">
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        const coordText = pin.coordType === 'DD' ? 
                          `${parseFloat(pin.lat).toFixed(6)}, ${parseFloat(pin.lng).toFixed(6)}` :
                          pin.coordType && pin.coordType.startsWith('UTM') && pin.zone && pin.easting && pin.northing ?
                          `${pin.zone} ${pin.easting}E ${pin.northing}N` :
                          `${parseFloat(pin.lat).toFixed(6)}, ${parseFloat(pin.lng).toFixed(6)}`;
                        navigator.clipboard.writeText(coordText);
                      }}
                      sx={{ 
                        fontSize: '0.65em', 
                        p: 0.25, 
                        color: '#2c8fdb',
                        minWidth: '22px',
                        minHeight: '22px'
                      }}
                    >
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Tooltip title="Remove pin">
                  <IconButton 
                    size="small" 
                    onClick={() => onRemovePin(pin.id)} 
                    sx={{ 
                      fontSize: '0.65em', 
                      p: 0.25, 
                      color: '#ff6b6b',
                      minWidth: '22px',
                      minHeight: '22px'
                    }}
                  >
                  <CloseIcon fontSize="small" />
                </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.8 }}>
            <Button 
              size="small" 
              onClick={onClearPins} 
              sx={{ 
                fontSize: '0.7rem',
                px: 2,
                py: 0.6,
                backgroundColor: 'rgba(255,107,107,0.15)',
                color: '#ff6b6b',
                border: '1.5px solid rgba(255,107,107,0.3)',
                borderRadius: '8px',
                fontWeight: 600,
                textTransform: 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255,107,107,0.25)',
                  borderColor: '#ff6b6b',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Clear All Pins
            </Button>
          </Box>
        </Box>
      )}

      {/* Error display */}
      {error && (
        <Box sx={{ mt: 1, p: 1, backgroundColor: 'rgba(255,0,0,0.1)', borderRadius: 1, border: '1px solid rgba(255,0,0,0.3)' }}>
          <Typography sx={{ color: '#ff6b6b', fontSize: '0.75rem', fontFamily: 'Louguiya, sans-serif' }}>
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {Draggable ? (
        <Draggable
          handle=".findxy-drag-handle"
          nodeRef={dragHandleRef}
          cancel="button, [role='button'], .MuiIconButton-root, input, select, .MuiInputBase-root, .MuiTextField-root, .MuiOutlinedInput-root, .MuiFormControl-root, .MuiSelect-root"
          bounds="body"
        >
<div
ref={dragHandleRef}
className="glass-pane-light"
style={{
      position: 'fixed',
top: '80px',
left: '50%',
transform: 'translateX(-50%)',
minWidth: '420px',
maxWidth: '460px',
width: '420px',
      zIndex: 3000,
filter: 'none',
background: 'rgba(24,29,38,0.85)',
backdropFilter: 'blur(10px)',
border: '1px solid rgba(255,255,255,0.12)',
borderRadius: '12px',
boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
overflow: 'hidden'
}}
>
<Box
className="findxy-drag-handle"
sx={{
cursor: 'move',
pt: 0.8,
pb: 0.3,
display: 'flex',
alignItems: 'center',
justifyContent: 'space-between',
px: 1.5,
gap: 1
}}
>
{/* Projection title on the left */}
<Typography sx={{
fontFamily: 'Louguiya, sans-serif',
fontWeight: 700,
fontSize: '0.95rem',
color: '#fff',
textShadow: '0 1px 2px rgba(0,0,0,0.5)'
}}>
Projection
</Typography>

{/* XY/Surface toggle */}
<Box sx={{
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
backgroundColor: 'rgba(255,255,255,0.1)',
borderRadius: '8px',
padding: '2px',
gap: 0,
fontFamily: 'Louguiya, sans-serif',
}}>
{['XY', 'Surface'].map((type) => (
<Box
key={type}
sx={{
px: 1,
py: 0.3,
borderRadius: 8,
fontWeight: 700,
fontSize: '0.78rem',
fontFamily: 'Louguiya, sans-serif',
color: coordType === (type === 'XY' ? 'POINT' : 'AREA') ? '#fff' : '#bbb',
backgroundColor: coordType === (type === 'XY' ? 'POINT' : 'AREA') ? 'rgba(255,255,255,0.2)' : 'transparent',
'&:hover': {
backgroundColor: coordType === (type === 'XY' ? 'POINT' : 'AREA') ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)',
},
cursor: 'pointer',
transition: 'all 0.2s ease',
}}
onClick={() => {
                const newCoordType = type === 'XY' ? 'POINT' : 'AREA';
                setCoordType(newCoordType);
                // Automatically switch to UTM 28 when switching to Surface mode
                // Keep user-selected CRS; do not force switch when entering Area mode
              }}
tabIndex={0}
role="button"
aria-pressed={coordType === (type === 'XY' ? 'POINT' : 'AREA')}
>
{type}
</Box>
))}
</Box>

{/* CRS Dropdown */}
<Box sx={{ position: 'relative' }}>
  <select
    value={coordinateFormat}
    onChange={(e) => setCoordinateFormat(e.target.value as 'DD' | 'UTM28' | 'UTM29' | 'UTM30')}
    style={{
      backgroundColor: 'rgba(255,255,255,0.1)',
      color: '#fff',
      border: '1.5px solid rgba(255,255,255,0.3)',
      borderRadius: '8px',
      padding: '4px 8px',
      fontSize: '0.75rem',
      fontFamily: 'Louguiya, sans-serif',
      fontWeight: 500,
      outline: 'none',
      cursor: 'pointer',
      minHeight: '32px',
      minWidth: '60px',
      appearance: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      textAlign: 'center',
      transition: 'all 0.2s ease',
    }}
    onFocus={(e) => {
      e.target.style.borderColor = '#2c8fdb';
      e.target.style.boxShadow = '0 0 8px rgba(44, 143, 219, 0.3)';
      e.target.style.backgroundColor = 'rgba(255,255,255,0.15)';
    }}
    onBlur={(e) => {
      e.target.style.borderColor = 'rgba(255,255,255,0.3)';
      e.target.style.boxShadow = 'none';
      e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
    }}
  >
    <option value="DD">DD</option>
    <option value="UTM28">UTM 28</option>
    <option value="UTM29">UTM 29</option>
    <option value="UTM30">UTM 30</option>
  </select>
</Box>

{/* Close button on the right */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: '#fff',
width: '24px',
height: '24px',
fontSize: '0.8rem',
'&:hover': {
backgroundColor: 'rgba(255,255,255,0.1)'
}
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {BoxContent}
</div>
      </Draggable>
) : (
<div
className="glass-pane-light"
style={{
position: 'fixed',
top: '80px',
left: '50%',
transform: 'translateX(-50%)',
minWidth: '420px',
maxWidth: '460px',
width: '420px',
zIndex: 3000,
filter: 'none',
background: 'rgba(24,29,38,0.85)',
backdropFilter: 'blur(10px)',
border: '1px solid rgba(255,255,255,0.12)',
borderRadius: '12px',
boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
overflow: 'hidden'
}}
>
<Box
className="findxy-drag-handle"
sx={{
cursor: 'move',
pt: 0.8,
pb: 0.3,
display: 'flex',
alignItems: 'center',
justifyContent: 'space-between',
px: 1.5,
gap: 1
}}
>
{/* Projection title on the left */}
<Typography sx={{
fontFamily: 'Louguiya, sans-serif',
fontWeight: 700,
fontSize: '0.95rem',
color: '#fff',
textShadow: '0 1px 2px rgba(0,0,0,0.5)'
}}>
Projection
</Typography>

{/* XY/Surface toggle */}
<Box sx={{
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
backgroundColor: 'rgba(255,255,255,0.1)',
borderRadius: '8px',
padding: '2px',
gap: 0,
fontFamily: 'Louguiya, sans-serif',
}}>
{['XY', 'Surface'].map((type) => (
<Box
key={type}
sx={{
px: 1,
py: 0.3,
borderRadius: 8,
fontWeight: 700,
fontSize: '0.78rem',
fontFamily: 'Louguiya, sans-serif',
color: coordType === (type === 'XY' ? 'POINT' : 'AREA') ? '#fff' : '#bbb',
backgroundColor: coordType === (type === 'XY' ? 'POINT' : 'AREA') ? 'rgba(255,255,255,0.2)' : 'transparent',
'&:hover': {
backgroundColor: coordType === (type === 'XY' ? 'POINT' : 'AREA') ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)',
},
cursor: 'pointer',
transition: 'all 0.2s ease',
}}
onClick={() => {
                const newCoordType = type === 'XY' ? 'POINT' : 'AREA';
                setCoordType(newCoordType);
                // Automatically switch to UTM 28 when switching to Surface mode
                if (newCoordType === 'AREA') {
                  setCoordinateFormat('UTM28');
                }
              }}
tabIndex={0}
role="button"
aria-pressed={coordType === (type === 'XY' ? 'POINT' : 'AREA')}
>
{type}
    </Box>
))}
</Box>

{/* CRS Dropdown */}
<Box sx={{ position: 'relative' }}>
  <select
    value={coordinateFormat}
    onChange={(e) => setCoordinateFormat(e.target.value as 'DD' | 'UTM28' | 'UTM29' | 'UTM30')}
    style={{
      backgroundColor: 'rgba(255,255,255,0.1)',
      color: '#fff',
      border: '1.5px solid rgba(255,255,255,0.3)',
      borderRadius: '8px',
      padding: '4px 8px',
      fontSize: '0.75rem',
      fontFamily: 'Louguiya, sans-serif',
      fontWeight: 500,
      outline: 'none',
      cursor: 'pointer',
      minHeight: '32px',
      minWidth: '60px',
      appearance: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      textAlign: 'center',
      transition: 'all 0.2s ease',
    }}
    onFocus={(e) => {
      e.target.style.borderColor = '#2c8fdb';
      e.target.style.boxShadow = '0 0 8px rgba(44, 143, 219, 0.3)';
      e.target.style.backgroundColor = 'rgba(255,255,255,0.15)';
    }}
    onBlur={(e) => {
      e.target.style.borderColor = 'rgba(255,255,255,0.3)';
      e.target.style.boxShadow = 'none';
      e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
    }}
  >
    <option value="DD">DD</option>
    <option value="UTM28">UTM 28</option>
    <option value="UTM29">UTM 29</option>
    <option value="UTM30">UTM 30</option>
  </select>
</Box>

{/* Close button on the right */}
<IconButton
onClick={onClose}
size="small"
sx={{
color: '#fff',
width: '24px',
height: '24px',
fontSize: '0.8rem',
'&:hover': {
backgroundColor: 'rgba(255,255,255,0.1)'
}
}}
>
<CloseIcon fontSize="small" />
</IconButton>
</Box>
{BoxContent}
</div>
)}
</>
);
};

export default React.forwardRef(CoordinateSearchComponent);
